import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomSelect from '../components/CustomSelect';
import './AccountAdd.css';
import { logout, checkAuth, handleAuthError } from '../utils/auth';
import { API_URL } from '../config/data';

const AccountAdd = () => {
  const [selectedBank, setSelectedBank] = useState('1');
  const [isManual, setIsManual] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('RUB');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Состояния для полей формы
  const [formData, setFormData] = useState({
    bank: '',
    bik_or_bankname: '',
    currency: 'RUB',
    number: '',
    bankname: '',
    inn: '',
    kpp: '',
    corp_bank_account: ''
  });

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Проверяем авторизацию перед загрузкой данных
    if (!checkAuth()) {
      return;
    }

    // Инициализируем поле bank значением по умолчанию
    const defaultBankText = bankOptions.find(option => option.value === selectedBank)?.text || '';
    handleFormDataChange('bank', defaultBankText);

    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const userData = await fetchUserProfile();
        setUser(userData);
      } catch (error) {
        console.error('Ошибка при загрузке профиля:', error);
        
        // Обрабатываем ошибки авторизации
        if (handleAuthError(error)) {
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для обновления данных формы
  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Функция для валидации формы
  const validateForm = () => {
    const newErrors = {};

    // Обязательные поля
    if (!formData.bik_or_bankname.trim()) {
      newErrors.bik_or_bankname = 'БИК или название банка обязательно';
    }
    if (!formData.number.trim()) {
      newErrors.number = 'Номер счета обязателен';
    }

    // Валидация ИНН (если заполнен)
    if (formData.inn && !/^\d{10}$|^\d{12}$/.test(formData.inn)) {
      newErrors.inn = 'ИНН должен содержать 10 или 12 цифр';
    }

    // Валидация КПП (если заполнен)
    if (formData.kpp && !/^\d{9}$/.test(formData.kpp)) {
      newErrors.kpp = 'КПП должен содержать 9 цифр';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Функция для отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      setSuccessMessage('');

      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      // Подготавливаем данные для отправки
      const submitData = {
        bank: formData.bank.trim(),
        bik_or_bankname: formData.bik_or_bankname.trim(),
        currency: selectedCurrency,
        number: formData.number.trim(),
        ...(formData.bankname && { bankname: formData.bankname.trim() }),
        ...(formData.inn && { inn: formData.inn.trim() }),
        ...(formData.kpp && { kpp: formData.kpp.trim() }),
        ...(formData.corp_bank_account && { corp_bank_account: formData.corp_bank_account.trim() })
      };

      const response = await axios.post(`${API_URL}/api/accounts`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.data.message === 'Bank account created successfully') {
        // Сбрасываем форму
        setFormData({
          bank: '',
          bik_or_bankname: '',
          currency: 'RUB',
          number: '',
          bankname: '',
          inn: '',
          kpp: '',
          corp_bank_account: ''
        });
        setSelectedCurrency('RUB');
        setSelectedBank('1');
        setIsManual(false);
        // Показываем модальное окно успеха
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error('Ошибка при создании счета:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      if (error.response?.data?.errors) {
        // Обрабатываем ошибки валидации с сервера
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          validationErrors[err.path] = err.msg;
        });
        setErrors(validationErrors);
      } else {
        setErrors({ general: 'Ошибка при создании счета. Попробуйте еще раз.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const bankOptions = [
    { value: '1', text: 'Другой банк' },
    { value: '2', text: 'Сбербанк' },
    { value: '3', text: 'Т-Банк' },
    { value: '4', text: 'Альфа-Банк' }
  ];

  // Функция для обработки изменения банка
  const handleBankChange = (value) => {
    setSelectedBank(value);
    const selectedBankText = bankOptions.find(option => option.value === value)?.text || '';
    handleFormDataChange('bank', selectedBankText);
  };

  const currencyOptions = [
    { value: 'RUB', text: 'RUB' },
    { value: 'USD', text: 'USD' },
    { value: 'EUR', text: 'EUR' },
    { value: 'CNY', text: 'CNY' }
  ];

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  // Обработчики для модального окна
  const handleSuccessPopupClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowSuccessPopup(false);
    }
  };

  const handleSuccessButtonClick = () => {
    setShowSuccessPopup(false);
  };

  // Обработчик для закрытия модального окна по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSuccessPopup) {
        setShowSuccessPopup(false);
      }
    };

    if (showSuccessPopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('popup-open');
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.classList.remove('popup-open');
      };
    }
  }, [showSuccessPopup]);

  // Показываем загрузку пока данные не загружены
  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--active aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="#" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">Добавление счета</h3>
              <div data-fls-maxwidth="" className="account__add add">
                <ul data-fls-filter="" className="add__filter filter">
                  {currencyOptions.map(currency => (
                    <li key={currency.value}>
                      <a 
                        href="#" 
                        className={`filter__link ${selectedCurrency === currency.value ? 'filter__link--active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedCurrency(currency.value);
                          handleFormDataChange('currency', currency.value);
                        }}
                      >
                        {currency.text}
                      </a>
                    </li>
                  ))}
                </ul>
                <form data-fls-form="" className="add__form" onSubmit={handleSubmit}>
                  {/* Сообщения об успехе и ошибках */}
                  {successMessage && (
                    <div className="add__success" style={{ color: 'green', marginBottom: '1rem' }}>
                      {successMessage}
                    </div>
                  )}
                  {errors.general && (
                    <div className="add__error" style={{ color: 'red', marginBottom: '1rem' }}>
                      {errors.general}
                    </div>
                  )}

                  <div data-fls-block="" className="add__inner">
                    <div className="add__select">
                      <div className="add__label-select">Банк</div>
                      <CustomSelect
                        options={bankOptions}
                        value={selectedBank}
                        onChange={handleBankChange}
                        modif="bank"
                        placeholder="Выберите банк"
                      />
                    </div>
                    <div className="add__toggle" onClick={() => setIsManual(!isManual)}>
                      <div className={`add__switch ${isManual ? 'active' : ''}`}></div>
                      <span>Заполнить вручную</span>
                    </div>
                    <div className="add__inputs">
                      {/* БИК или название банка (обязательное) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          required 
                          autoComplete="off" 
                          type="text" 
                          name="bik_or_bankname" 
                          value={formData.bik_or_bankname}
                          onChange={(e) => handleFormDataChange('bik_or_bankname', e.target.value)}
                          data-error="Error" 
                          placeholder="БИК или название банка *" 
                          className={`add__input input ${errors.bik_or_bankname ? 'input--error' : ''}`}
                        />
                        {errors.bik_or_bankname && <div className="add__field-error">{errors.bik_or_bankname}</div>}
                      </div>

                      {/* Номер счета (обязательное) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          required
                          autoComplete="off" 
                          type="text" 
                          name="number" 
                          value={formData.number}
                          onChange={(e) => handleFormDataChange('number', e.target.value)}
                          data-error="Error" 
                          placeholder="Номер счета *" 
                          className={`add__input input ${errors.number ? 'input--error' : ''}`}
                        />
                        {errors.number && <div className="add__field-error">{errors.number}</div>}
                      </div>

                      {/* Полное название банка (опциональное, только в ручном режиме) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          autoComplete="off" 
                          type="text" 
                          name="bankname" 
                          value={formData.bankname}
                          onChange={(e) => handleFormDataChange('bankname', e.target.value)}
                          data-error="Error" 
                          placeholder="Полное название банка" 
                          className={`add__input input ${!isManual ? 'input--disable' : ''}`}
                          disabled={!isManual}
                        />
                      </div>

                      {/* ИНН (опциональное, только в ручном режиме) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          autoComplete="off" 
                          type="text" 
                          name="inn" 
                          value={formData.inn}
                          onChange={(e) => handleFormDataChange('inn', e.target.value)}
                          data-error="Error" 
                          placeholder="ИНН (10 или 12 цифр)" 
                          className={`add__input input ${!isManual ? 'input--disable' : ''} ${errors.inn ? 'input--error' : ''}`}
                          disabled={!isManual}
                          maxLength="12"
                        />
                        {errors.inn && <div className="add__field-error">{errors.inn}</div>}
                      </div>

                      {/* КПП (опциональное, только в ручном режиме) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          autoComplete="off" 
                          type="text" 
                          name="kpp" 
                          value={formData.kpp}
                          onChange={(e) => handleFormDataChange('kpp', e.target.value)}
                          data-error="Error" 
                          placeholder="КПП (9 цифр)" 
                          className={`add__input input ${!isManual ? 'input--disable' : ''} ${errors.kpp ? 'input--error' : ''}`}
                          disabled={!isManual}
                          maxLength="9"
                        />
                        {errors.kpp && <div className="add__field-error">{errors.kpp}</div>}
                      </div>

                      {/* Корпоративный счет банка (опциональное, только в ручном режиме) */}
                      <div>
                        <input 
                          data-fls-input="" 
                          autoComplete="off" 
                          type="text" 
                          name="corp_bank_account" 
                          value={formData.corp_bank_account}
                          onChange={(e) => handleFormDataChange('corp_bank_account', e.target.value)}
                          data-error="Error" 
                          placeholder="Корпоративный счет банка" 
                          className={`add__input input ${!isManual ? 'input--disable' : ''}`}
                          disabled={!isManual}
                        />
                      </div>
                    </div>
                  </div>
                  <button 
                    type="submit" 
                    data-fls-button="" 
                    className="add__button button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Создание...' : 'Добавить счет'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно успеха */}
      {showSuccessPopup && (
        <div 
          data-fls-popup="success" 
          aria-hidden="false" 
          className={`popup ${showSuccessPopup ? 'show' : ''}`} 
          onClick={handleSuccessPopupClose}
        >
          <div data-fls-popup-wrapper="" className="popup__wrapper">
            <div data-fls-popup-body="" className="popup__body success">
              <h3 className="success__title success__title--title-20">Успешно!</h3>
              <div className="success__content">
                <p className="success__message">
                  Банковский счет успешно добавлен! Теперь вы можете использовать его для операций.
                </p>
              </div>
              <div className="success__actions">
                <button 
                  data-fls-button="" 
                  type="button" 
                  className="success__button button"
                  onClick={handleSuccessButtonClick}
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountAdd;